import { z } from 'zod';
import { API_TOKEN_ACCESS, API_TOKEN_KINDS, type ApiTokenAccess, type ApiTokenKind } from '../enums';
import { idSchema } from './common';

/**
 * Tokeny API (Fala 5; ARCHITECTURE §11, PERMISSIONS §3.5; tabela `api_tokens`, `packages/db/src/schema/identity.ts`).
 *
 * Trzy rodzaje (`kind`):
 *  - `pat` — osobisty token MCP (`fos_pat_…`): Claude Code, inne agenty zgodne z MCP. Przypisany do osoby i workspace'u,
 *    z zakresem (wszystko albo wybrani klienci / projekty), poziomem (`read` / `write`), datą wygaśnięcia
 *    i natychmiastowym cofnięciem. Działa tylko na `/mcp`.
 *  - `runner` — token lokalnego runnera (`fos_rnr_…`): tylko `/api/runner/*` (docs/RUNNER_PROTOCOL.md). Powiązany
 *    z rekordem `runners` (`runners.token_id`), właściciel tokenu = właściciel komputera.
 *  - `run` — token przebiegu (`fos_run_…`): wydawany przy `claim`, krótko żyjący (limit czasu runu + 15 min), zakres =
 *    projekt runu (bez projektu: klient runu), tylko narzędzia runu (`agent_runs.allowed_tools`), `api_tokens.run_id`.
 *    Działa tylko na `/mcp`. Cofany przy zakończeniu runu.
 *  - `oauth` — połączenie konektora (Claude w claude.ai, aplikacji mobilnej i Desktop; OAuth 2.1, docs/MCP_TOOLS.md §4.3):
 *    wiersz powstaje po zgodzie osoby (ekran `/polacz`) i wymianie kodu; zakres, poziom i ważność jak przy `pat`. Klient
 *    dostaje krótkie tokeny dostępu `fos_oat_…` (`oauth_tokens`, 1 h) i odświeżania `fos_ort_…` (rotacja). Token dostępu
 *    działa na `/mcp` DOKŁADNIE jak token osobisty tej osoby (`tokenId` = to połączenie); cofnięcie połączenia w
 *    Ustawieniach kończy dostęp i odświeżanie od następnego żądania.
 *
 * Zasady (wspólne): w bazie tylko SHA-256 tokenu (`token_hash`) i jawny prefiks do rozpoznania (`fos_pat_ab12`);
 * surowy token pokazujemy raz, przy utworzeniu. Każde żądanie: token istnieje, nie cofnięty, nie wygasł, członkostwo
 * osoby aktywne, konto nie wyłączone → świeży AccessProfile osoby zawężony tokenem (`apiTokenRestriction`) —
 * odebranie dostępu działa od następnego żądania. Tworzenie i cofanie tokenów: tylko z UI (AI / MCP = FORBIDDEN),
 * step-up 10 min, audyt. Dezaktywacja osoby cofa jej tokeny (`team.deactivate`).
 */

export { API_TOKEN_ACCESS, API_TOKEN_KINDS, type ApiTokenAccess, type ApiTokenKind };
export const apiTokenKindSchema = z.enum(API_TOKEN_KINDS);
export const apiTokenAccessSchema = z.enum(API_TOKEN_ACCESS);

// ── Format ──────────────────────────────────────────────────────────────────

/** Jawne prefiksy (rozpoznawalne w logach i skanerach sekretów). */
export const API_TOKEN_PREFIXES = {
  pat: 'fos_pat_',
  runner: 'fos_rnr_',
  run: 'fos_run_',
  oauth: 'fos_oat_',
} as const satisfies Record<ApiTokenKind, string>;

/** Losowa część: 32 B → base64url (43 znaki), jak `generateToken()` z `@fos/core/identity/tokens`. */
export const API_TOKEN_SECRET_BYTES = 32;
export const API_TOKEN_SECRET_LENGTH = 43;
/** Ile znaków losowej części pokazujemy w prefiksie (`fos_pat_ab12`). */
export const API_TOKEN_DISPLAY_CHARS = 4;

const KIND_BY_TAG: Record<string, ApiTokenKind> = { pat: 'pat', rnr: 'runner', run: 'run', oat: 'oauth' };
const TOKEN_RE = /^fos_(pat|rnr|run|oat)_([A-Za-z0-9_-]{43})$/;

/** Surowy token z losowej części: `formatApiToken('pat', generateToken())` → `fos_pat_…`. */
export function formatApiToken(kind: ApiTokenKind, secret: string): string {
  if (!/^[A-Za-z0-9_-]{43}$/.test(secret)) throw new Error('Nieprawidłowa losowa część tokenu');
  return `${API_TOKEN_PREFIXES[kind]}${secret}`;
}

/** Rozpoznanie tokenu (bez bazy); `null` = to nie jest token Fravity OS. Hash liczy się z całego surowego tokenu. */
export function parseApiToken(raw: string | null | undefined): { kind: ApiTokenKind; token: string } | null {
  if (typeof raw !== 'string') return null;
  const token = raw.trim();
  const m = TOKEN_RE.exec(token);
  const kind = m?.[1] ? KIND_BY_TAG[m[1]] : undefined;
  return kind ? { kind, token } : null;
}

/** Jawny prefiks do zapisu w `api_tokens.prefix` i do UI: `fos_pat_ab12`. */
export function apiTokenDisplayPrefix(token: string): string {
  const parsed = parseApiToken(token);
  if (!parsed) throw new Error('Nieprawidłowy token');
  const head = API_TOKEN_PREFIXES[parsed.kind];
  return token.slice(0, head.length + API_TOKEN_DISPLAY_CHARS);
}

/** Token z nagłówka `Authorization: Bearer …` (inne schematy i puste = `null`). */
export function bearerToken(header: string | string[] | null | undefined): string | null {
  const value = Array.isArray(header) ? header[0] : header;
  if (typeof value !== 'string') return null;
  const m = /^Bearer\s+(\S+)\s*$/i.exec(value.trim());
  return m?.[1] ?? null;
}

/** Gdzie dany rodzaj tokenu działa (inny rodzaj = 401 UNAUTHENTICATED bez zdradzania, czy token istnieje). */
export const API_TOKEN_SURFACES = {
  pat: 'mcp',
  run: 'mcp',
  runner: 'runner',
  oauth: 'mcp',
} as const satisfies Record<ApiTokenKind, 'mcp' | 'runner'>;

// ── Zakres ──────────────────────────────────────────────────────────────────

/**
 * Zakres tokenu w bazie (`api_tokens.scope`): `{ all: true }` albo wybrani klienci i projekty. Pusty wybór jest
 * dozwolony tylko dla tokenu przebiegu (run bez projektu i klienta — widzi tylko swój run).
 */
export const apiTokenScopeSchema = z.union([
  z.object({ all: z.literal(true) }).strict(),
  z
    .object({
      all: z.literal(false),
      clientIds: z.array(idSchema).max(200).default([]),
      projectIds: z.array(idSchema).max(200).default([]),
    })
    .strict(),
]);
export type ApiTokenScope = z.infer<typeof apiTokenScopeSchema>;

/** Zakres przy tworzeniu tokenu osobistego: wszystko albo co najmniej jeden klient / projekt. */
export const personalTokenScopeInput = apiTokenScopeSchema.refine(
  (s) => s.all || s.clientIds.length + s.projectIds.length > 0,
  { message: 'Wybierz co najmniej jednego klienta albo projekt' },
);

/**
 * Zawężenie uprawnień osoby tokenem (kształt `AccessRestriction` z `@fos/core/access`; część wspólna, nigdy szerzej).
 * Token z zakresem (nie „wszystko”) NIE widzi rekordów wewnętrznych osoby (bez klienta, projektu i zasobu) —
 * `internal: false`; widzi je tylko token z zakresem „wszystko”. Token `read` obniża moduły do podglądu.
 */
export function apiTokenRestriction(token: { access: ApiTokenAccess | string; scope: unknown }): {
  access: ApiTokenAccess;
  scope: { all: true } | { all: false; clientIds: string[]; projectIds: string[]; internal: false };
} {
  const access: ApiTokenAccess = token.access === 'write' ? 'write' : 'read';
  const parsed = apiTokenScopeSchema.safeParse(token.scope);
  // nieczytelny zakres = nic (bezpiecznie), nigdy „wszystko”
  if (!parsed.success) return { access, scope: { all: false, clientIds: [], projectIds: [], internal: false } };
  const s = parsed.data;
  if (s.all) return { access, scope: { all: true } };
  return { access, scope: { all: false, clientIds: s.clientIds, projectIds: s.projectIds, internal: false } };
}

/** Zakres tokenu przebiegu: projekt runu; bez projektu — klient runu; bez obu — nic poza samym runem. */
export function runTokenScope(run: { projectId: string | null; clientId: string | null }): ApiTokenScope {
  if (run.projectId) return { all: false, clientIds: [], projectIds: [run.projectId] };
  if (run.clientId) return { all: false, clientIds: [run.clientId], projectIds: [] };
  return { all: false, clientIds: [], projectIds: [] };
}

// ── Ważność ─────────────────────────────────────────────────────────────────

/** Token osobisty: wybór w UI; domyślnie 90 dni, najwyżej rok (bez „bezterminowo”). */
export const PERSONAL_TOKEN_EXPIRY_DAYS = [7, 30, 90, 365] as const;
export const PERSONAL_TOKEN_DEFAULT_EXPIRY_DAYS = 90;
/** Token runnera: domyślnie rok (komputer z runnerem), krócej na żądanie. */
export const RUNNER_TOKEN_EXPIRY_DAYS = [30, 90, 365] as const;
export const RUNNER_TOKEN_DEFAULT_EXPIRY_DAYS = 365;
/** Token przebiegu: limit czasu runu + zapas na testy, diff i wysyłkę wyniku; najwyżej 8 h 15 min. */
export const RUN_TOKEN_GRACE_SEC = 15 * 60;
export const RUN_TOKEN_MAX_TTL_SEC = 8 * 3600 + RUN_TOKEN_GRACE_SEC;
/** `last_used_at` / `last_used_ip` aktualizowane najwyżej raz na minutę (bez zapisu przy każdym żądaniu). */
export const API_TOKEN_TOUCH_INTERVAL_SEC = 60;

export function tokenExpiresAt(from: Date, days: number): Date {
  return new Date(from.getTime() + days * 86_400_000);
}

/** Wygaśnięcie tokenu przebiegu liczone od startu runu (`claim`). */
export function runTokenExpiresAt(startedAt: Date, maxMinutes: number): Date {
  const ttl = Math.min(Math.max(1, maxMinutes) * 60 + RUN_TOKEN_GRACE_SEC, RUN_TOKEN_MAX_TTL_SEC);
  return new Date(startedAt.getTime() + ttl * 1000);
}

export const API_TOKEN_STATUSES = ['active', 'expired', 'revoked'] as const;
export type ApiTokenStatus = (typeof API_TOKEN_STATUSES)[number];

/** Stan tokenu do UI i do decyzji przy żądaniu (cofnięty ma pierwszeństwo przed wygasłym). */
export function apiTokenStatus(t: { revokedAt: Date | null; expiresAt: Date | null }, now: Date): ApiTokenStatus {
  if (t.revokedAt) return 'revoked';
  if (t.expiresAt && t.expiresAt.getTime() <= now.getTime()) return 'expired';
  return 'active';
}

// ── Wejścia usług (UI: Ustawienia → Tokeny MCP; tylko z UI, step-up) ───────

export const apiTokenNameSchema = z
  .string()
  .trim()
  .min(1, { message: 'Nazwa jest wymagana' })
  .max(80, { message: 'Najwyżej 80 znaków' });

export const createPersonalTokenInput = z
  .object({
    name: apiTokenNameSchema,
    access: apiTokenAccessSchema.default('read'),
    scope: personalTokenScopeInput,
    expiresInDays: z
      .number()
      .int()
      .refine((d) => (PERSONAL_TOKEN_EXPIRY_DAYS as readonly number[]).includes(d), {
        message: 'Wybierz ważność: 7, 30, 90 albo 365 dni',
      })
      .default(PERSONAL_TOKEN_DEFAULT_EXPIRY_DAYS),
    /**
     * „Może zlecać pracę na moim komputerze” (Fala 6): `create_agent_run` z `runtime: local_runner` przez MCP.
     * Domyślnie wyłączone; tylko z poziomem `write` (inaczej VALIDATION_ERROR — usługa `tokens.create`).
     */
    localRunnerAllowed: z.boolean().default(false),
  })
  .strict();
export type CreatePersonalTokenInput = z.input<typeof createPersonalTokenInput>;

export const listApiTokensInput = z
  .object({
    /** `mcp` = tokeny osobiste i połączenia konektorów (lista „Tokeny MCP”) */
    kind: z.enum(['pat', 'runner', 'oauth', 'mcp']).optional(),
    /** owner/admin: tokeny wszystkich osób (np. po odejściu osoby); pozostali tylko swoje */
    allMembers: z.boolean().default(false),
    includeInactive: z.boolean().default(false),
  })
  .strict();

export const revokeApiTokenInput = z.object({ id: idSchema }).strict();

// ── DTO ─────────────────────────────────────────────────────────────────────

export interface ApiTokenScopeDto {
  all: boolean;
  clients: { id: string; name: string }[];
  projects: { id: string; name: string; clientName: string | null }[];
}

/** Token w listach (bez hasha i bez surowego tokenu). */
export interface ApiTokenDto {
  id: string;
  kind: ApiTokenKind;
  name: string;
  /** jawny prefiks, np. `fos_pat_ab12` */
  prefix: string;
  access: ApiTokenAccess;
  scope: ApiTokenScopeDto;
  status: ApiTokenStatus;
  user: { id: string; name: string };
  createdAt: Date;
  expiresAt: Date | null;
  lastUsedAt: Date | null;
  lastUsedIp: string | null;
  revokedAt: Date | null;
  runId: string | null;
  /** „Może zlecać pracę na moim komputerze” (tylko token osobisty z zapisem) */
  localRunnerAllowed: boolean;
}

/** Wynik utworzenia: surowy token tylko tutaj, jeden raz. */
export interface ApiTokenCreated {
  token: ApiTokenDto;
  secret: string;
}

export const TOKEN_MESSAGES = {
  missing: 'Brak tokenu w nagłówku Authorization',
  invalid: 'Nieprawidłowy albo nieaktywny token',
  wrongSurface: 'Ten rodzaj tokenu nie działa w tym miejscu',
  readOnly: 'Token tylko do odczytu — ta operacja wymaga tokenu z zapisem',
  toolNotAllowed: 'To narzędzie nie jest dozwolone dla tego przebiegu',
  showOnce: 'Skopiuj token teraz — nie pokażemy go ponownie.',
  revoked: 'Token cofnięty. Kolejne żądania z nim zostaną odrzucone.',
  uiOnly: 'Tokeny tworzy i cofa się tylko w aplikacji',
  localRunnerNotAllowed:
    'Ten token nie może zlecać pracy na Twoim komputerze. Utwórz token z opcją „Może zlecać pracę na moim komputerze” (Ustawienia → Tokeny i agenci) albo zleć pracę z aplikacji („Zleć Claude”).',
  localRunnerRequiresWrite: 'Zlecanie pracy na komputerze wymaga poziomu „Odczyt i zapis”',
} as const;
